import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { getOrderMiraklMessages, replyOrderMiraklMessage, syncMiraklOrderMessages, getMessaggioTrackingOrdine } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faTruck, faPaperclip, faTimes } from '@fortawesome/free-solid-svg-icons';
import api from '../api';
import './OrderMessages.css'; // We'll create this CSS file for styling

const formatDimensione = (b) => (b == null ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function OrderMessages({ ordineId, onMessagesLoaded }) {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [files, setFiles] = useState([]); // allegati da inviare con la risposta
  const [dest, setDest] = useState('CUSTOMER'); // a chi va la risposta: CUSTOMER / OPERATOR / BOTH
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Gli allegati richiedono il token: si scaricano come blob dal backend (che fa da proxy verso Mirakl)
  const scaricaAllegato = async (a) => {
    try {
      const res = await api.get(`/mirakl/allegati/${a.id}/download`, { params: { nome: a.name }, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.name || 'allegato';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Impossibile scaricare ${a.name || 'l\'allegato'}.`);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = useCallback(async (isSilent = false) => {
    if (!isSilent) {
      setLoading(true);
    }
    setError(null);
    try {
      // Forza la sincronizzazione dei messaggi da Mirakl prima di caricarli
      if (ordineId) { // Sincronizza solo se c'è un ordine ID locale
          await syncMiraklOrderMessages(ordineId);
      }
      const res = await getOrderMiraklMessages(ordineId);
      const newMessages = res.data || []; // Corretto: res.data è già la lista dei messaggi
      setMessages(newMessages);

    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Errore caricamento messaggi';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg).join('; ');
      } else if (errorDetail && typeof errorDetail === 'object') {
         errorMessage = JSON.stringify(errorDetail); // Fallback per oggetti complessi
      } else if (err.message) {
         errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      if (!isSilent) {
        setLoading(false);
      }
    }
  }, [ordineId]);

  useEffect(() => {
    if (ordineId) {
      fetchMessages();
    } else {
      setMessages([]);
    }
  }, [fetchMessages, ordineId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const lastThreadId = messages.length > 0 ? messages[messages.length - 1].mirakl_thread_id : null;

    if (!lastThreadId) {
      setError("Impossibile trovare un thread a cui rispondere. Sincronizza i messaggi.");
      return;
    }

          setSending(true);

        setError(null);

        try {

          // I partecipanti li ricava il backend dal thread, filtrati per tipo scelto
          const tipi = dest === 'BOTH' ? null : [dest];
          if (files.length > 0) {
            const fd = new FormData();
            fd.append('message_body', newMessage);
            if (tipi) fd.append('tipi', JSON.stringify(tipi));
            files.forEach(f => fd.append('files', f));
            await api.post(`/mirakl/orders/${ordineId}/threads/${lastThreadId}/reply-allegati`, fd);
          } else {
            await replyOrderMiraklMessage(ordineId, lastThreadId, newMessage, null, tipi);
          }



          setNewMessage('');
          setFiles([]);

          await fetchMessages(true); // Silent refresh

        } catch (err) {

          const errorDetail = err.response?.data?.detail;

          let errorMessage = 'Errore invio messaggio';

          if (typeof errorDetail === 'string') {

            errorMessage = errorDetail;

          } else if (Array.isArray(errorDetail)) {

            errorMessage = errorDetail.map(e => e.msg).join('; ');

          } else if (errorDetail && typeof errorDetail === 'object') {

             errorMessage = JSON.stringify(errorDetail); // Fallback per oggetti complessi

          } else if (err.message) {

             errorMessage = err.message;

          }

          setError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleInserisciTemplateTracking = async () => {
    if (newMessage.trim() && !window.confirm('Sostituire il testo gia\' scritto con il template tracking?')) {
      return;
    }
    setLoadingTemplate(true);
    setError(null);
    try {
      const res = await getMessaggioTrackingOrdine(ordineId);
      setNewMessage(res.data.messaggio);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel recupero del template tracking');
    } finally {
      setLoadingTemplate(false);
    }
  };

  if (!ordineId) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <div className="text-muted">Seleziona una conversazione per vedere la chat.</div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <div className="chat-messages-container flex-grow-1 p-3">
        {loading && messages.length === 0 ? (
          <div className="text-center"><Spinner animation="border" /></div>
        ) : (
          messages.length === 0 ? (
            <div className="text-center text-muted">Nessun messaggio in questa conversazione.</div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`message-bubble-row ${m.sender_type === 'OPERATOR' ? 'sent' : 'received'}`}>
                <div className="message-bubble">
                  <div className="message-sender">{m.sender_name || m.sender_type}</div>
                  <div className="message-body" dangerouslySetInnerHTML={{ __html: m.body }} />
                  {(m.allegati || []).length > 0 && (
                    <div className="mt-1">
                      {m.allegati.map(a => (
                        <Button key={a.id} size="sm" variant="outline-secondary" className="me-1 mb-1" onClick={() => scaricaAllegato(a)}>
                          <FontAwesomeIcon icon={faPaperclip} className="me-1" />
                          {a.name} {a.size != null && <span className="text-muted">({formatDimensione(a.size)})</span>}
                        </Button>
                      ))}
                    </div>
                  )}
                  <div className="message-time">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container p-2 border-top">
        {ordineId && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="mb-2"
            onClick={handleInserisciTemplateTracking}
            disabled={loadingTemplate}
          >
            <FontAwesomeIcon icon={faTruck} spin={loadingTemplate} className="me-1" />
            Inserisci aggiornamento tracking
          </Button>
        )}
        {files.length > 0 && (
          <div className="mb-2">
            {files.map((f, i) => (
              <span key={i} className="badge bg-light text-dark border me-1">
                <FontAwesomeIcon icon={faPaperclip} className="me-1" />{f.name}
                <FontAwesomeIcon icon={faTimes} className="ms-2" style={{ cursor: 'pointer' }} onClick={() => setFiles(files.filter((_, j) => j !== i))} />
              </span>
            ))}
          </div>
        )}
        <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => {
          // FileList va copiata subito: azzerare value la svuota prima che React esegua l'updater
          const scelti = Array.from(e.target.files);
          e.target.value = '';
          setFiles(prev => [...prev, ...scelti]);
        }} />
        <Button variant="outline-secondary" size="sm" className="mb-2 ms-2" onClick={() => fileInputRef.current?.click()}>
          <FontAwesomeIcon icon={faPaperclip} className="me-1" />Allega file
        </Button>
        <Form.Select size="sm" className="mb-2 w-auto d-inline-block ms-2" value={dest} onChange={(e) => setDest(e.target.value)} disabled={sending} aria-label="Destinatario">
          <option value="CUSTOMER">Rispondi al cliente</option>
          <option value="OPERATOR">Rispondi all'operatore IBS</option>
          <option value="BOTH">Rispondi a cliente e operatore</option>
        </Form.Select>
        <Form onSubmit={handleSend} className="d-flex">
          <Form.Control
            as="textarea"
            rows={2}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Scrivi una risposta..."
            disabled={sending}
          />
          <div className="ms-2 d-flex flex-column">
             <Button variant="primary" type="submit" disabled={sending || !newMessage.trim()}>
              {sending ? <Spinner as="span" animation="border" size="sm" /> : 'Invia'}
            </Button>
            <Button size="sm" variant="light" onClick={() => fetchMessages(false)} className="mt-1" disabled={loading}>
              <FontAwesomeIcon icon={faSync} spin={loading} />
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
